// import dependencies
import { v4 as uuid } from 'uuid'
import { type StoreSafeObject, type ITransportIpc } from '@highlight/ipc'
import {
  createRendererTransport,
  describeTrustedIpcSender,
  getOID,
  isRenderer,
  type IpcSenderEvent,
  type TrustedIpcSender,
} from '@highlight/ipc/utils'
import { AppWindowType } from '@shared/types/WindowTypes'

type IpcActionCallback = (payload: StoreSafeObject, sender?: Electron.WebContents) => Promise<any> | any

interface IpcActionRequest {
  action: string
  actionId: string
  payload?: StoreSafeObject
}

interface IpcError {
  _ipcError: { code: string; message: string; stack: string; name: string }
}

/**
 * The renderer pages that may call routers: the `AppWindowType` values, which
 * must equal the HTML entry basenames main loads for app windows, plus the
 * hidden `audio-runtime` page, which fetches its transcription token through
 * the auth router and applies speaker renames through the audio-note router.
 * `challenge` is not an app page, so its calls are refused.
 */
const ROUTER_PAGES: ReadonlySet<string> = new Set([...Object.values(AppWindowType), 'audio-runtime'])

function ipcError(message: string, err?: unknown): IpcError {
  const e = err as { code?: string; message?: string; stack?: string; name?: string } | undefined
  return {
    _ipcError: {
      code: e?.code ?? '',
      message: e?.message ?? message,
      stack: e?.stack ?? '',
      name: e?.name ?? '',
    },
  }
}

function isIpcActionRequest(value: unknown): value is IpcActionRequest {
  return typeof value === 'object' && value !== null && typeof (value as { action?: unknown }).action === 'string'
}

/**
 * The IpcRouter class wraps all the ipcMain and ipcRenderer functionality to more easily
 * register IPC actions in the main process, and execute to them + subscribe to them
 * from the renderer process.
 *
 * `T` is the router's interface, and `actions` names each of its keys. A renderer
 * can call only those, never another public method of the main-process subclass.
 */
class IpcRouter<T> {
  private readonly _actions: ReadonlySet<string>
  private _listening: boolean
  readonly _namespace: string
  private readonly _ipcChannel: string
  private readonly _ipcTransport?: ITransportIpc | Electron.IpcRenderer
  private readonly _processId: string

  constructor(namespace: string, actions: Record<keyof T, true>, channel?: string) {
    if (!namespace) {
      throw new Error("'namespace' is required when instantiating a new IpcRouter")
    }

    this._namespace = namespace
    this._ipcChannel = channel ?? 'router'
    this._actions = new Set(Object.keys(actions))
    this._listening = false
    this._processId = getOID()

    if (isRenderer) {
      // Check if the preload script has defined an ipc events transport
      if (typeof window.ipc?.channels !== 'undefined') {
        this._ipcTransport = window.ipc.channels[this._ipcChannel]
      } else {
        // Otherwise, assume we are running with nodeIntegration enabled (not recommended)
        // In which case, we need to create the renderer transport inline.
        this._ipcTransport = createRendererTransport(this._ipcChannel)
      }

      if (!this._ipcTransport) {
        throw new Error(`Error resolving ipc transport for channel: ${this._ipcChannel}`)
      }
    }
  }

  private getActionHandler(action: string): IpcActionCallback | undefined {
    if (!this._actions.has(action)) {
      return undefined
    }
    const method = (this as unknown as Record<string, unknown>)[action]
    return typeof method === 'function' ? (method as IpcActionCallback).bind(this) : undefined
  }

  protected describeSender(event: IpcSenderEvent): TrustedIpcSender | null {
    return describeTrustedIpcSender(event)
  }

  /** Why main refuses router calls from this sender, or `null` when it accepts them. */
  private senderRefusal(event: IpcSenderEvent): string | null {
    const sender = this.describeSender(event)
    if (!sender) {
      return 'untrusted sender'
    }
    if (!ROUTER_PAGES.has(sender.page)) {
      return `page "${sender.page}" may not call IPC routers`
    }
    return null
  }

  /**
   * Run a renderer's action request in main and return what goes back over the
   * wire: the action's result, or an `_ipcError` the renderer rethrows.
   */
  async handleRendererCall(event: IpcSenderEvent, request: unknown): Promise<unknown> {
    if (!isIpcActionRequest(request)) {
      return ipcError('Unrecognized IPC action')
    }

    const refusal = this.senderRefusal(event)
    if (refusal) {
      console.warn(`[IpcRouter] refused ${this._namespace}:${request.action} (${refusal})`)
      return ipcError(`IPC action refused: ${refusal}`)
    }

    const handler = this.getActionHandler(request.action)
    if (!handler) {
      return ipcError(`Unrecognized IPC action: ${request.action}`)
    }

    try {
      return await handler(request.payload as StoreSafeObject, event.sender)
    } catch (err) {
      console.error('Error executing ipc action:', request.action, err)
      return ipcError('Error executing ipc action', err)
    }
  }

  /**
   * Called to instantiate the router in the Main process.
   */
  listen(ipcMain?: Pick<Electron.IpcMain, 'on'>) {
    if (isRenderer) {
      return
    }
    if (this._listening) {
      return
    }

    this._listening = true

    // Required lazily because renderers bundle this class too.
    const target = ipcMain ?? (require('electron') as typeof import('electron')).ipcMain

    target.on(
      `${this._ipcChannel}:${this._namespace}:async`,
      async (event: Electron.IpcMainEvent, _oid: string, request: IpcActionRequest | undefined) => {
        // determine the response channel
        const response = event.reply ? event.reply : event.sender.send.bind(event.sender) // @todo some backwards compatibility thing for electron -- not sure if needed anymore
        const result = await this.handleRendererCall(event, request)
        response(`${this._ipcChannel}:${this._namespace}:async:${request?.actionId}`, result)
      },
    )

    target.on(
      `${this._ipcChannel}:${this._namespace}:sync`,
      async (event: Electron.IpcMainEvent, _oid: string, request: IpcActionRequest | undefined) => {
        event.returnValue = await this.handleRendererCall(event, request)
      },
    )
  }

  /**
   * Execute an IPC action on the main thread and return the results asynchronously.
   * @param action
   * @param payload
   * @returns {*}
   */
  exec<K extends keyof T & string>(action: K, payload?: StoreSafeObject) {
    if (!isRenderer) {
      const handler = this.getActionHandler(action)
      if (handler) {
        return handler(payload as StoreSafeObject)
      } else {
        console.error('Cannot exec IPC action from main process:', action, payload)
      }
    } else {
      return new Promise((resolve, reject) => {
        const actionId = uuid()
        this._ipcTransport!.once(`${this._namespace}:async:${actionId}`, (response) => {
          if (response && typeof response === 'object' && response._ipcError) {
            const error = new Error(response._ipcError.message)
            error.stack = response._ipcError.stack
            error.name = response._ipcError.name
            if (response._ipcError.code) Object.assign(error, { code: response._ipcError.code })
            reject(error)
            return
          }
          resolve(response)
        })
        this._ipcTransport!.send(`${this._namespace}:async`, this._processId, {
          action,
          actionId,
          payload,
        })
      })
    }
  }

  /**
   * Execute an IPC action on the main thread and return the results synchronously.
   * @param action
   * @param payload
   * @returns {any}
   */
  execSync<K extends keyof T & string>(action: K, payload?: StoreSafeObject) {
    if (!isRenderer) {
      const handler = this.getActionHandler(action)
      if (handler) {
        return handler(payload as StoreSafeObject)
      } else {
        console.error('Cannot exec IPC action from main process:', payload)
      }
    } else {
      const actionId = uuid()

      const response = this._ipcTransport!.sendSync(`${this._namespace}:sync`, this._processId, {
        action,
        actionId,
        payload,
      })

      if (response?._ipcError) {
        const error = new Error(response._ipcError.message)
        error.stack = response._ipcError.stack
        error.name = response._ipcError.name
        throw error
      }

      return response
    }
  }
}

export default IpcRouter
